import React from "react";
import { DashboardLayout } from "../components/dashboard/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { apiRequest, ApiError } from "../api/client";
import { formatCurrency } from "../utils/currency";
import { BudgetApi, Category } from "../types/dashboard";
import { MONTH_NAMES } from "../constants/dates";
import {
  IconPiggyBank,
  IconTarget,
  IconTrendingDown,
  IconTrendingUp,
} from "../components/common/WireframeIcons";

type SavingsSection = "configuration" | "simulation" | "tracking";

interface YearSummary {
  income: number;
  expense: number;
  net: number;
}

interface CategorySummaryItem {
  categoryId: number | null;
  categoryName: string;
  kind: "income" | "expense" | "transfer" | "uncategorized";
  total: number;
}

interface ExpenseSummaryItem extends CategorySummaryItem {
  totalAbs: number;
}

interface MonthlyCategorySummaryItem extends CategorySummaryItem {
  month: number;
}

interface CategorySummaryResponse {
  yearly: CategorySummaryItem[];
  monthly: MonthlyCategorySummaryItem[];
}

type BudgetsResponse = BudgetApi[] | { budgets: BudgetApi[]; plannedMonthlyIncome?: number | null };

function extractErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

export default function SavingsPlanPage() {
  const { token, logout } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = React.useState<number>(currentYear);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [yearSummary, setYearSummary] = React.useState<YearSummary | null>(null);
  const [expenseSummary, setExpenseSummary] = React.useState<ExpenseSummaryItem[]>([]);
  const [monthlyCategorySummary, setMonthlyCategorySummary] = React.useState<MonthlyCategorySummaryItem[]>([]);
  const [budgetDraft, setBudgetDraft] = React.useState<Record<number, string>>({});
  const [serverBudgets, setServerBudgets] = React.useState<BudgetApi[]>([]);
  const [savingBudgets, setSavingBudgets] = React.useState(false);
  const [savingPlannedIncome, setSavingPlannedIncome] = React.useState(false);
  const [saveMessage, setSaveMessage] = React.useState<string | null>(null);
  const [addCategoryId, setAddCategoryId] = React.useState<string>("");
  const [removedBudgetIds, setRemovedBudgetIds] = React.useState<number[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const approachPct = 60;
  const [activeSection, setActiveSection] = React.useState<SavingsSection>("configuration");
  const [plannedMonthlyIncome, setPlannedMonthlyIncome] = React.useState<string>("");
  const currentMonth = new Date().getMonth() + 1;

  const years = React.useMemo(() => Array.from({ length: 6 }, (_, index) => currentYear - index), [currentYear]);

  const handleRequest = React.useCallback(
    async <T,>(promise: Promise<T>): Promise<T> => {
      try {
        return await promise;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          logout();
        }
        throw error;
      }
    },
    [logout]
  );

  const loadData = React.useCallback(
    async (selectedYear: number) => {
      if (!token) return;
      setError(null);
      try {
        const [cats, totals, categorySummary, budgets] = await Promise.all([
          handleRequest(apiRequest<Category[]>("/categories", { token })),
          handleRequest(apiRequest<YearSummary>(`/transactions/summary/year?year=${selectedYear}`, { token })),
          handleRequest(apiRequest<CategorySummaryResponse>(`/transactions/summary/categories?year=${selectedYear}`, { token })),
          handleRequest(apiRequest<BudgetsResponse>("/budgets", { token })),
        ]);

        setCategories(cats);
        setYearSummary(totals);
        const budgetList = Array.isArray(budgets) ? budgets : budgets?.budgets ?? [];
        setServerBudgets(budgetList);
        if (!Array.isArray(budgets) && budgets && Object.prototype.hasOwnProperty.call(budgets, "plannedMonthlyIncome")) {
          const planned = budgets.plannedMonthlyIncome;
          setPlannedMonthlyIncome(planned === null || planned === undefined ? "" : Number(planned).toFixed(0));
        }
        setMonthlyCategorySummary(categorySummary.monthly ?? []);

        const expenses: ExpenseSummaryItem[] = categorySummary.yearly
          .filter(item => item.kind === "expense" && item.total < 0)
          .map(item => ({
            ...item,
            totalAbs: Math.abs(item.total),
          }))
          .sort((a, b) => b.totalAbs - a.totalAbs);
        setExpenseSummary(expenses);

        const monthlyStats = new Map<number, { min: number; max: number }>();
        (categorySummary.monthly ?? [])
          .filter(item => item.kind === "expense" && item.categoryId !== null && item.total < 0)
          .forEach(item => {
            const categoryId = item.categoryId as number;
            const amountAbs = Math.abs(item.total);
            const existing = monthlyStats.get(categoryId);
            if (!existing) {
              monthlyStats.set(categoryId, { min: amountAbs, max: amountAbs });
            } else {
              existing.min = Math.min(existing.min, amountAbs);
              existing.max = Math.max(existing.max, amountAbs);
            }
          });

        const budgetFromServer = new Map<number, number>(
          budgetList.map(item => [item.category_id, Number(item.monthly_limit)])
        );

        const initialBudgetEntries = expenses
          .filter(item => item.categoryId !== null)
          .map(item => {
            const existing = budgetFromServer.get(item.categoryId as number);
            const monthlyAvg = item.totalAbs / 12;
            const stats = monthlyStats.get(item.categoryId as number);
            const minMonthly = stats?.min ?? monthlyAvg;
            const maxMonthly = stats?.max ?? monthlyAvg;
            const recommendedMonthly = maxMonthly - (maxMonthly - minMonthly) * (approachPct / 100);
            const fallback = Number.isFinite(recommendedMonthly) ? recommendedMonthly : monthlyAvg;
            return [item.categoryId as number, (existing ?? fallback).toFixed(0)];
          });

        // Incluye presupuestos existentes aunque la categoría no tenga gasto en el año
        for (const budget of budgetList) {
          if (!initialBudgetEntries.some(([id]) => id === budget.category_id)) {
            initialBudgetEntries.push([budget.category_id, Number(budget.monthly_limit).toFixed(0)]);
          }
        }

        const initialBudget = Object.fromEntries(initialBudgetEntries);
        setBudgetDraft(initialBudget);
      } catch (loadError) {
        setError(extractErrorMessage(loadError, "No pudimos obtener los datos para el plan de ahorro."));
        setYearSummary(null);
        setExpenseSummary([]);
        setMonthlyCategorySummary([]);
        setBudgetDraft({});
        setServerBudgets([]);
        setRemovedBudgetIds([]);
      }
    },
    [token, handleRequest]
  );

  React.useEffect(() => {
    if (!token) return;
    loadData(year);
  }, [token, year, loadData]);

  const topExpenseCategories = React.useMemo(() => expenseSummary.slice(0, 10), [expenseSummary]);

  const monthlyExpenseRanges = React.useMemo(() => {
    const ranges = new Map<number, { min: number; max: number }>();
    monthlyCategorySummary
      .filter(item => item.kind === "expense" && item.categoryId !== null && item.total < 0)
      .forEach(item => {
        const categoryId = item.categoryId as number;
        const amountAbs = Math.abs(item.total);
        const entry = ranges.get(categoryId);
        if (!entry) {
          ranges.set(categoryId, { min: amountAbs, max: amountAbs });
        } else {
          entry.min = Math.min(entry.min, amountAbs);
          entry.max = Math.max(entry.max, amountAbs);
        }
      });
    return ranges;
  }, [monthlyCategorySummary]);

  const projectedSavings = React.useMemo(() => {
    let monthlySavings = 0;
    topExpenseCategories.forEach(item => {
      const draft = budgetDraft[item.categoryId as number];
      const monthlyCurrent = item.totalAbs / 12;
      if (draft && !Number.isNaN(Number(draft))) {
        monthlySavings += Math.max(0, monthlyCurrent - Number(draft));
      }
    });
    return monthlySavings;
  }, [topExpenseCategories, budgetDraft]);

  const persistBudgets = async (
    nextDraft: Record<number, string>,
    nextRemovedBudgetIds: number[],
    successMessage = "Presupuesto actualizado automáticamente"
  ) => {
    if (!token) return;
    setSavingBudgets(true);
    setSaveMessage(null);

    const budgetsPayload = Object.entries(nextDraft)
      .map(([categoryId, value]) => ({ categoryId: Number(categoryId), monthlyLimit: Number(value) }))
      .filter(item => Number.isFinite(item.monthlyLimit) && item.monthlyLimit >= 0);

    try {
      if (budgetsPayload.length) {
        await handleRequest(apiRequest<BudgetsResponse>("/budgets", {
          method: "PUT",
          token,
          body: { budgets: budgetsPayload },
        }));
      }

      if (nextRemovedBudgetIds.length) {
        await handleRequest(apiRequest<void>("/budgets", {
          method: "DELETE",
          token,
          body: { categoryIds: nextRemovedBudgetIds },
        }));
      }

      const fresh = await handleRequest(apiRequest<BudgetsResponse>("/budgets", { token }));
      const budgetList = Array.isArray(fresh) ? fresh : fresh?.budgets ?? [];
      setServerBudgets(budgetList);
      setRemovedBudgetIds([]);
      setSaveMessage(successMessage);
    } catch (saveError) {
      setSaveMessage(extractErrorMessage(saveError, "No pudimos guardar el presupuesto"));
    } finally {
      setSavingBudgets(false);
    }
  };

  const handleAddCategory = async () => {
    const parsed = Number(addCategoryId);
    if (!Number.isInteger(parsed) || parsed <= 0) return;
    const existing = budgetDraft[parsed];
    const serverExisting = serverBudgets.find(item => item.category_id === parsed);
    const fallback = serverExisting ? serverExisting.monthly_limit : 0;
    const nextDraft = { ...budgetDraft, [parsed]: existing ?? String(fallback) };
    const nextRemovedBudgetIds = removedBudgetIds.filter(id => id !== parsed);
    setBudgetDraft(nextDraft);
    setRemovedBudgetIds(nextRemovedBudgetIds);
    setAddCategoryId("");
    await persistBudgets(nextDraft, nextRemovedBudgetIds);
  };

  const handleRemoveCategory = async (categoryId: number) => {
    const nextDraft = { ...budgetDraft } as Record<number, string>;
    delete nextDraft[categoryId];
    const nextRemovedBudgetIds =
      serverBudgets.some(b => b.category_id === categoryId) && !removedBudgetIds.includes(categoryId)
        ? [...removedBudgetIds, categoryId]
        : removedBudgetIds;
    setBudgetDraft(nextDraft);
    setRemovedBudgetIds(nextRemovedBudgetIds);
    await persistBudgets(nextDraft, nextRemovedBudgetIds);
  };

  const expenseCategories = React.useMemo(
    () => categories.filter(category => category.kind === "expense"),
    [categories]
  );

  const budgetRows = React.useMemo(() => {
    const expenseById = new Map<number, ExpenseSummaryItem>(
      expenseSummary.filter(item => item.categoryId !== null).map(item => [item.categoryId as number, item])
    );

    return Object.entries(budgetDraft)
      .map(([key, value]) => {
        const categoryId = Number(key);
        const expense = expenseById.get(categoryId);
        const category = expenseCategories.find(cat => cat.id === categoryId);
        const name = category?.name ?? expense?.categoryName ?? `Categoría #${categoryId}`;
        const annual = expense?.totalAbs ?? 0;
        const monthly = expense ? expense.totalAbs / 12 : 0;
        const range = monthlyExpenseRanges.get(categoryId);
        const minMonthly = range?.min ?? monthly;
        const maxMonthly = range?.max ?? monthly;
        return {
          categoryId,
          name,
          annual,
          monthly,
          minMonthly,
          maxMonthly,
          draft: value,
        };
      })
      .sort((a, b) => b.monthly - a.monthly);
  }, [budgetDraft, expenseSummary, expenseCategories, monthlyExpenseRanges]);

  const currentMonthByCategory = React.useMemo(() => {
    const map = new Map<number, number>();
    monthlyCategorySummary
      .filter(item => item.kind === "expense" && item.categoryId !== null && item.total < 0 && item.month === currentMonth)
      .forEach(item => {
        const categoryId = item.categoryId as number;
        map.set(categoryId, (map.get(categoryId) ?? 0) + Math.abs(item.total));
      });
    return map;
  }, [monthlyCategorySummary, currentMonth]);

  const availableCategories = React.useMemo(
    () => expenseCategories.filter(cat => !(cat.id in budgetDraft)),
    [expenseCategories, budgetDraft]
  );

  const savingsTips = React.useMemo(
    () => [
      {
        title: "Automatiza tu ahorro",
        description:
          "Programa transferencias automáticas periódicas desde tu cuenta corriente a una cuenta de ahorro para mantener consistencia.",
        sourceLabel: "CFPB · Emergency fund guide",
        sourceUrl: "https://www.consumerfinance.gov/an-essential-guide-to-building-an-emergency-fund/",
      },
      {
        title: "Págate primero cada mes",
        description:
          "Reserva una cantidad fija de ahorro en cuanto cobras, antes de empezar a gastar en otras partidas.",
        sourceLabel: "MyMoney.gov · Save and Invest",
        sourceUrl: "https://www.mymoney.gov/saveandinvest",
      },
      {
        title: "Crea un fondo de emergencia",
        description:
          "Prioriza acumular un colchón de al menos 3 meses de gastos esenciales en una cuenta accesible.",
        sourceLabel: "MyMoney.gov · Save and Invest",
        sourceUrl: "https://www.mymoney.gov/saveandinvest",
      },
      {
        title: "Revisa tu gasto real y ajusta el presupuesto",
        description:
          "Comparar lo presupuestado vs lo ejecutado cada mes ayuda a detectar desviaciones y corregir hábitos a tiempo.",
        sourceLabel: "MyMoney.gov · Spend",
        sourceUrl: "https://www.mymoney.gov/spend",
      },
      {
        title: "Usa alertas para evitar comisiones y descubrir fugas",
        description:
          "Activa avisos de saldo y movimientos para prevenir descubiertos y detectar suscripciones o cargos recurrentes innecesarios.",
        sourceLabel: "FDIC · Save, Organize, and Streamline",
        sourceUrl: "https://www.fdic.gov/consumer-resource-center/2022-02/save-organize-and-streamline-your-finances",
      },
    ],
    []
  );

  const categoryCards = React.useMemo(
    () =>
      budgetRows.map(row => {
        const budgetValue = Number(row.draft);
        const hasBudget = row.draft !== "" && Number.isFinite(budgetValue) && budgetValue >= 0;
        const monthlyBudget = hasBudget ? budgetValue : 0;
        const currentMonthTotal = currentMonthByCategory.get(row.categoryId) ?? 0;
        const remaining = monthlyBudget - currentMonthTotal;
        const percentUsed = monthlyBudget > 0 ? (currentMonthTotal / monthlyBudget) * 100 : 0;
        return {
          ...row,
          currentMonthTotal,
          monthlyBudget,
          remaining,
          percentUsed,
          hasBudget,
        };
      }),
    [budgetRows, currentMonthByCategory]
  );

  const kpis = React.useMemo(() => {
    const yearlyExpenses = Math.abs(yearSummary?.expense ?? 0);
    const totalBudget = categoryCards.reduce((acc, row) => acc + row.monthlyBudget, 0);
    const currentMonthSpent = categoryCards.reduce((acc, row) => acc + row.currentMonthTotal, 0);
    const remaining = totalBudget - currentMonthSpent;
    return {
      totalBudget,
      currentMonthSpent,
      remaining,
      yearlyExpenses,
      yearlyIncome: yearSummary?.income ?? 0,
      yearlyNet: yearSummary?.net ?? 0,
    };
  }, [categoryCards, yearSummary]);

  const budgetCategoryIds = React.useMemo(() => new Set(categoryCards.map(row => row.categoryId)), [categoryCards]);

  const monthlyBudgetConfigured = React.useMemo(
    () => categoryCards.reduce((acc, row) => acc + row.monthlyBudget, 0),
    [categoryCards]
  );

  const monthlySimulationRows = React.useMemo(() => {
    const rows = Array.from({ length: 12 }, (_, index) => ({
      month: index + 1,
      incomeReal: 0,
      expenseBudgetedReal: 0,
      expenseUnbudgetedReal: 0,
    }));

    monthlyCategorySummary.forEach(item => {
      if (item.month < 1 || item.month > 12) return;
      const row = rows[item.month - 1];
      if (item.kind === "income") {
        row.incomeReal += item.total;
        return;
      }
      if (item.kind !== "expense" || item.total >= 0) return;
      const amount = Math.abs(item.total);
      if (item.categoryId !== null && budgetCategoryIds.has(item.categoryId)) {
        row.expenseBudgetedReal += amount;
      } else {
        row.expenseUnbudgetedReal += amount;
      }
    });

    return rows.map(row => {
      const realExpenseTotal = row.expenseBudgetedReal + row.expenseUnbudgetedReal;
      const simulatedExpenseTotal = monthlyBudgetConfigured + row.expenseUnbudgetedReal;
      const realSavings = row.incomeReal - realExpenseTotal;
      const simulatedSavings = row.incomeReal - simulatedExpenseTotal;
      return {
        ...row,
        realExpenseTotal,
        simulatedExpenseTotal,
        realSavings,
        simulatedSavings,
        deltaSavings: simulatedSavings - realSavings,
      };
    });
  }, [monthlyCategorySummary, budgetCategoryIds, monthlyBudgetConfigured]);

  const simulationTotals = React.useMemo(
    () =>
      monthlySimulationRows.reduce(
        (acc, row) => ({
          incomeReal: acc.incomeReal + row.incomeReal,
          expenseBudgetedReal: acc.expenseBudgetedReal + row.expenseBudgetedReal,
          expenseUnbudgetedReal: acc.expenseUnbudgetedReal + row.expenseUnbudgetedReal,
          simulatedExpenseTotal: acc.simulatedExpenseTotal + row.simulatedExpenseTotal,
          realSavings: acc.realSavings + row.realSavings,
          simulatedSavings: acc.simulatedSavings + row.simulatedSavings,
          deltaSavings: acc.deltaSavings + row.deltaSavings,
        }),
        {
          incomeReal: 0,
          expenseBudgetedReal: 0,
          expenseUnbudgetedReal: 0,
          simulatedExpenseTotal: 0,
          realSavings: 0,
          simulatedSavings: 0,
          deltaSavings: 0,
        }
      ),
    [monthlySimulationRows]
  );

  React.useEffect(() => {
    if (activeSection === "tracking" && year !== currentYear) {
      setYear(currentYear);
    }
  }, [activeSection, year, currentYear]);

  React.useEffect(() => {
    if (plannedMonthlyIncome.trim() !== "") return;
    const suggested = (yearSummary?.income ?? 0) / 12;
    if (Number.isFinite(suggested) && suggested > 0) {
      setPlannedMonthlyIncome(suggested.toFixed(0));
    }
  }, [yearSummary, plannedMonthlyIncome]);

  const trackingRows = React.useMemo(
    () =>
      monthlySimulationRows.map(row => {
        const parsed = Number(plannedMonthlyIncome);
        const plannedIncome = Number.isFinite(parsed) ? parsed : row.incomeReal;
        const availableWithPlan = plannedIncome - (monthlyBudgetConfigured + row.expenseUnbudgetedReal);
        return {
          ...row,
          plannedIncome,
          availableWithPlan,
        };
      }),
    [monthlySimulationRows, plannedMonthlyIncome, monthlyBudgetConfigured]
  );

  const trackingTotals = React.useMemo(
    () =>
      trackingRows.reduce(
        (acc, row) => ({
          plannedIncome: acc.plannedIncome + row.plannedIncome,
          incomeReal: acc.incomeReal + row.incomeReal,
          expenseUnbudgetedReal: acc.expenseUnbudgetedReal + row.expenseUnbudgetedReal,
          availableWithPlan: acc.availableWithPlan + row.availableWithPlan,
          realSavings: acc.realSavings + row.realSavings,
        }),
        { plannedIncome: 0, incomeReal: 0, expenseUnbudgetedReal: 0, availableWithPlan: 0, realSavings: 0 }
      ),
    [trackingRows]
  );

  const handlePlannedIncomeChange = (value: string) => {
    if (value.trim() === "") {
      setPlannedMonthlyIncome("");
      return;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    setPlannedMonthlyIncome(value);
  };

  const persistPlannedMonthlyIncome = async () => {
    if (!token) return;
    const trimmed = plannedMonthlyIncome.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) {
      setSaveMessage("El ingreso planificado debe ser un número positivo");
      return;
    }

    setSavingPlannedIncome(true);
    setSaveMessage(null);
    try {
      const response = await handleRequest(
        apiRequest<BudgetsResponse>("/budgets", {
          method: "PUT",
          token,
          body: { plannedMonthlyIncome: parsed },
        })
      );
      const budgetList = Array.isArray(response) ? response : response?.budgets ?? [];
      setServerBudgets(budgetList);
      if (!Array.isArray(response) && response && Object.prototype.hasOwnProperty.call(response, "plannedMonthlyIncome")) {
        const persisted = response.plannedMonthlyIncome;
        setPlannedMonthlyIncome(persisted === null || persisted === undefined ? "" : Number(persisted).toFixed(0));
      }
      setSaveMessage("Ingreso mensual planificado guardado");
    } catch (saveError) {
      setSaveMessage(extractErrorMessage(saveError, "No pudimos guardar el ingreso planificado"));
    } finally {
      setSavingPlannedIncome(false);
    }
  };

  const handleBudgetInputChange = (categoryId: number, value: string) => {
    if (value.trim() === "") {
      setBudgetDraft(prev => ({ ...prev, [categoryId]: "" }));
      return;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    setBudgetDraft(prev => ({ ...prev, [categoryId]: value }));
  };

  const handleBudgetInputCommit = async (categoryId: number) => {
    const rawValue = budgetDraft[categoryId] ?? "";
    const normalizedRaw = rawValue.trim() === "" ? "0" : rawValue;
    const parsed = Number(normalizedRaw);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    const nextDraft = { ...budgetDraft, [categoryId]: parsed.toFixed(0) };
    setBudgetDraft(nextDraft);
    await persistBudgets(nextDraft, removedBudgetIds);
  };

  return (
    <DashboardLayout active="savings">
      <div className="operations-page-header">
        <div className="operations-page-header-inner">
          <div className="operations-page-heading">
            <h1>Plan de Ahorro</h1>
            <p>Define presupuestos por categoría y alcanza tus metas</p>
          </div>
        </div>
      </div>

      <div className="savings-sections-strip">
        <div className="savings-sections-inner">
          <div className="settings-subnav savings-subnav" role="tablist" aria-label="Secciones del plan de ahorro">
            <button
              className={`settings-subnav-button${activeSection === "configuration" ? " is-active" : ""}`}
              type="button"
              onClick={() => setActiveSection("configuration")}
            >
              <IconTarget className="w-4 h-4" />
              Configuración del plan
            </button>
            <button
              className={`settings-subnav-button${activeSection === "simulation" ? " is-active" : ""}`}
              type="button"
              onClick={() => setActiveSection("simulation")}
            >
              <IconTrendingUp className="w-4 h-4" />
              Simulación histórica
            </button>
            <button
              className={`settings-subnav-button${activeSection === "tracking" ? " is-active" : ""}`}
              type="button"
              onClick={() => setActiveSection("tracking")}
            >
              <IconPiggyBank className="w-4 h-4" />
              Seguimiento año actual
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-page-content is-fullwidth operations-page-body grid" style={{ gap: "1.35rem" }}>
        {error && (
          <div
            className="panel"
            style={{ border: "1px solid rgba(220,38,38,0.25)", background: "rgba(254, 226, 226, 0.65)", color: "#b91c1c" }}
          >
            {error}
          </div>
        )}

        {activeSection === "tracking" && (
          <section className="savings-kpi-strip">
            {yearSummary ? (
              <div className="savings-summary-grid">
                <div className="savings-summary-card">
                  <div className="savings-summary-icon tone-blue">
                    <IconTarget className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="savings-summary-label">Presupuesto total</p>
                    <p className="savings-summary-value">{formatCurrency(-kpis.totalBudget)}</p>
                  </div>
                </div>

                <div className="savings-summary-card">
                  <div className="savings-summary-icon tone-red">
                    <IconTrendingDown className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="savings-summary-label">Gastado este mes</p>
                    <p className="savings-summary-value negative">{formatCurrency(-kpis.currentMonthSpent)}</p>
                  </div>
                </div>

                <div className="savings-summary-card">
                  <div className={`savings-summary-icon ${kpis.remaining >= 0 ? "tone-green" : "tone-red"}`}>
                    <IconPiggyBank className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="savings-summary-label">Restante</p>
                    <p className={`savings-summary-value ${kpis.remaining >= 0 ? "positive" : "negative"}`}>
                      {formatCurrency(kpis.remaining >= 0 ? -kpis.remaining : Math.abs(kpis.remaining))}
                    </p>
                  </div>
                </div>

                <div className="savings-summary-card">
                  <div className="savings-summary-icon tone-green">
                    <IconTrendingUp className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="savings-summary-label">Ahorro proyectado</p>
                    <p className={`savings-summary-value ${projectedSavings >= 0 ? "positive" : "negative"}`}>
                      {formatCurrency(projectedSavings)}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">Aún no hay datos anuales para proponer un plan.</div>
            )}
          </section>
        )}

        {activeSection === "configuration" && (
          <>
            <section className="savings-income-config">
              <div className="panel-header">
                <h4 className="panel-title">Ingresos mensuales planificados</h4>
                <span className="chip">Se aplican en Seguimiento año actual</span>
              </div>
              <div className="savings-income-single-row">
                <label htmlFor="planned-monthly-income">Total ingreso mensual</label>
                <input
                  id="planned-monthly-income"
                  type="number"
                  className="input"
                  value={plannedMonthlyIncome}
                  onChange={event => handlePlannedIncomeChange(event.target.value)}
                  onBlur={() => {
                    void persistPlannedMonthlyIncome();
                  }}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void persistPlannedMonthlyIncome();
                    }
                  }}
                  placeholder="0"
                  disabled={savingPlannedIncome}
                />
              </div>
            </section>

            <section className="savings-budget-panel">
              <div className="savings-budget-header">
                <div>
                  <h3 className="panel-title">Presupuestos por Categoría</h3>
                  <p className="panel-subtitle">Indica lo que quieres gastar en cada categoría</p>
                </div>
                <div className="savings-budget-header-actions">
                  <select
                    className="input"
                    value={addCategoryId}
                    onChange={event => setAddCategoryId(event.target.value)}
                  >
                    <option value="">Presupuestar categoría</option>
                    {availableCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={handleAddCategory}
                    disabled={!addCategoryId || savingBudgets}
                  >
                    Incluir
                  </button>
                </div>
              </div>
              {expenseSummary.length ? (
                <>
                  <div className="savings-wireframe-list">
                    {categoryCards.map(row => {
                      const categoryColor = expenseCategories.find(category => category.id === row.categoryId)?.color;

                      return (
                        <article className="savings-wireframe-row" key={row.categoryId}>
                          <div className="savings-wireframe-main">
                            <div className="savings-wireframe-title-row">
                              <span
                                className="category-color-dot"
                                style={categoryColor ? { backgroundColor: categoryColor } : undefined}
                              />
                              <h4>{row.name}</h4>
                            </div>
                          </div>

                          <div className="savings-wireframe-budget">
                            <div className="savings-wireframe-budget-fields">
                              <div className="savings-wireframe-budget-top">
                                <div className="savings-wireframe-stats savings-wireframe-stats-inline">
                                  <span>Promedio: {formatCurrency(-row.monthly)}</span>
                                  <span>Mín: {formatCurrency(-row.minMonthly)}</span>
                                  <span>Máx: {formatCurrency(-row.maxMonthly)}</span>
                                </div>
                                <div className="savings-wireframe-budget-input-row">
                                  <label htmlFor={`budget-${row.categoryId}`}>Presupuesto mensual</label>
                                  <input
                                    id={`budget-${row.categoryId}`}
                                    type="number"
                                    className="input"
                                    disabled={savingBudgets}
                                    value={budgetDraft[row.categoryId] ?? ""}
                                    onChange={event => handleBudgetInputChange(row.categoryId, event.target.value)}
                                    onBlur={() => handleBudgetInputCommit(row.categoryId)}
                                    onKeyDown={event => {
                                      if (event.key === "Enter") {
                                        event.preventDefault();
                                        void handleBudgetInputCommit(row.categoryId);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="savings-wireframe-actions">
                            <button
                              className="btn btn-light"
                              type="button"
                              disabled={savingBudgets}
                              onClick={() => handleRemoveCategory(row.categoryId)}
                            >
                              Excluir
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                </>
              ) : (
                <div className="empty-state">No hay datos suficientes para calcular presupuestos.</div>
              )}
              <div className="savings-save-row">
                <span className="chip">
                  {savingBudgets || savingPlannedIncome
                    ? "Guardando cambios..."
                    : saveMessage ?? "Cambios automáticos activados"}
                </span>
              </div>
            </section>

          </>
        )}

        {activeSection === "simulation" && (
          <section>
            <div className="panel-header">
              <h3 className="panel-title">Simulación histórica</h3>
              <div className="savings-year-filter">
                <label htmlFor="savings-simulation-year">Año</label>
                <select
                  id="savings-simulation-year"
                  className="input"
                  style={{ minWidth: "132px" }}
                  value={year}
                  onChange={event => setYear(Number(event.target.value))}
                >
                  {years.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="panel-subtitle">
              Compara ahorro real vs ahorro simulado aplicando el presupuesto mensual sobre categorías incluidas.
            </p>
            <div className="annual-matrix-wrapper" style={{ marginTop: "0.8rem" }}>
              <table className="table savings-analysis-table">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th>Ingresos reales</th>
                    <th>Gasto real (presupuestadas)</th>
                    <th>Gasto real (no presupuestadas)</th>
                    <th>Gasto simulado con plan</th>
                    <th>Ahorro real</th>
                    <th>Ahorro simulado</th>
                    <th>Diferencia</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlySimulationRows.map(row => (
                    <tr key={`sim-${row.month}`}>
                      <th>{MONTH_NAMES[row.month - 1]}</th>
                      <td>{formatCurrency(row.incomeReal)}</td>
                      <td>{formatCurrency(-row.expenseBudgetedReal)}</td>
                      <td>{formatCurrency(-row.expenseUnbudgetedReal)}</td>
                      <td>{formatCurrency(-row.simulatedExpenseTotal)}</td>
                      <td className={row.realSavings >= 0 ? "positive" : "negative"}>{formatCurrency(row.realSavings)}</td>
                      <td className={row.simulatedSavings >= 0 ? "positive" : "negative"}>{formatCurrency(row.simulatedSavings)}</td>
                      <td className={row.deltaSavings >= 0 ? "positive" : "negative"}>{formatCurrency(row.deltaSavings)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th>Total</th>
                    <td>{formatCurrency(simulationTotals.incomeReal)}</td>
                    <td>{formatCurrency(-simulationTotals.expenseBudgetedReal)}</td>
                    <td>{formatCurrency(-simulationTotals.expenseUnbudgetedReal)}</td>
                    <td>{formatCurrency(-simulationTotals.simulatedExpenseTotal)}</td>
                    <td className={simulationTotals.realSavings >= 0 ? "positive" : "negative"}>
                      {formatCurrency(simulationTotals.realSavings)}
                    </td>
                    <td className={simulationTotals.simulatedSavings >= 0 ? "positive" : "negative"}>
                      {formatCurrency(simulationTotals.simulatedSavings)}
                    </td>
                    <td className={simulationTotals.deltaSavings >= 0 ? "positive" : "negative"}>
                      {formatCurrency(simulationTotals.deltaSavings)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        )}

        {activeSection === "tracking" && (
          <section className="panel">
            <div className="panel-header">
              <h3 className="panel-title">Seguimiento del plan ({currentYear})</h3>
              <span className="chip">Presupuesto mensual fijo: {formatCurrency(-monthlyBudgetConfigured)}</span>
            </div>
            <p className="panel-subtitle">
              Revisa el disponible mensual del plan con los ingresos planificados definidos en Configuración del plan.
            </p>
            <div className="annual-matrix-wrapper" style={{ marginTop: "0.8rem" }}>
              <table className="table savings-analysis-table">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th>Ingreso real</th>
                    <th>Ingreso planificado</th>
                    <th>Presupuesto categ. incluidas</th>
                    <th>Gasto real no presupuestado</th>
                    <th>Disponible con plan</th>
                    <th>Ahorro real</th>
                  </tr>
                </thead>
                <tbody>
                  {trackingRows.map(row => (
                    <tr key={`tracking-${row.month}`}>
                      <th>{MONTH_NAMES[row.month - 1]}</th>
                      <td>{formatCurrency(row.incomeReal)}</td>
                      <td>{formatCurrency(row.plannedIncome)}</td>
                      <td>{formatCurrency(-monthlyBudgetConfigured)}</td>
                      <td>{formatCurrency(-row.expenseUnbudgetedReal)}</td>
                      <td className={row.availableWithPlan >= 0 ? "positive" : "negative"}>
                        {formatCurrency(row.availableWithPlan)}
                      </td>
                      <td className={row.realSavings >= 0 ? "positive" : "negative"}>{formatCurrency(row.realSavings)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th>Total anual</th>
                    <td>{formatCurrency(trackingTotals.incomeReal)}</td>
                    <td>{formatCurrency(trackingTotals.plannedIncome)}</td>
                    <td>{formatCurrency(-(monthlyBudgetConfigured * 12))}</td>
                    <td>{formatCurrency(-trackingTotals.expenseUnbudgetedReal)}</td>
                    <td className={trackingTotals.availableWithPlan >= 0 ? "positive" : "negative"}>
                      {formatCurrency(trackingTotals.availableWithPlan)}
                    </td>
                    <td className={trackingTotals.realSavings >= 0 ? "positive" : "negative"}>
                      {formatCurrency(trackingTotals.realSavings)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        )}

        <section className="savings-tips-box">
          <h3>💡 Consejos para ahorrar</h3>
          <ul>
            {savingsTips.map(tip => (
              <li key={tip.title}>
                • {tip.description}{" "}
                <a href={tip.sourceUrl} target="_blank" rel="noreferrer">
                  ({tip.sourceLabel})
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </DashboardLayout>
  );
}
